import { opponentLogoUrl, type Fixture } from '@/api/fixtures';

// MatchSide is one half of a fixture — a name and the crest that belongs to it.
// Our side carries the club's own logo, theirs the opponent's; both may be null,
// in which case the renderer falls back to a monogram.
export interface MatchSide {
  name: string;
  logoUrl: string | null;
}

// MatchDisplay is a fixture reduced to what a calendar row needs to render a
// game rather than a generic activity: the two sides already ordered home-first
// and each carrying its own crest, where we sit in the tie, and the score once
// recorded. Shared by every calendar surface so "A-lag – IFK Göteborg" reads
// identically on the home widget, the schedule grid, and anywhere else it lands.
export interface MatchDisplay {
  home: MatchSide;
  away: MatchSide;
  // Which end of the tie we're on — drives the "Home"/"Away" label. The names
  // alone can't answer it once both sides look alike.
  isHome: boolean;
  competition: string;
  homeScore?: number;
  awayScore?: number;
}

// matchDisplay builds the row model, or returns null when there's no game to
// show yet. `ours` is our team's name (the activity's first team, falling back
// to its title) plus the club's own crest. A `match` activity with no opponent
// set is a TBD fixture: callers render it as a plain activity row instead of
// half a scoreboard.
export function matchDisplay(
  fixture: Fixture | undefined,
  ours: MatchSide,
): MatchDisplay | null {
  if (!fixture?.opponent_name) return null;
  const theirs: MatchSide = {
    name: fixture.opponent_name,
    logoUrl: opponentLogoUrl(fixture),
  };
  const scored =
    fixture.result_status === 'final' &&
    fixture.home_score != null &&
    fixture.away_score != null;
  return {
    home: fixture.is_home ? ours : theirs,
    away: fixture.is_home ? theirs : ours,
    isHome: fixture.is_home,
    competition: fixture.competition_name ?? '',
    ...(scored ? { homeScore: fixture.home_score, awayScore: fixture.away_score } : {}),
  };
}

// scoreLabel renders the recorded score home-first, or null when the match
// hasn't been played (or the result wasn't entered).
export function scoreLabel(m: MatchDisplay): string | null {
  if (m.homeScore == null || m.awayScore == null) return null;
  return `${m.homeScore}–${m.awayScore}`;
}
